// Calls to the app's own server, formatting, and small browser helpers.
import type { Lang } from './i18n.ts'

/** A 4xx or 5xx answer. `code` is the server's `{ error }`. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly body: Readonly<Record<string, unknown>>
  constructor(status: number, code: string, body: Readonly<Record<string, unknown>>) {
    super(`${status} ${code}`)
    this.status = status
    this.code = code
    this.body = body
  }
}
/** The request never reached the server (offline, DNS, connection reset). */
export class NetworkError extends Error {}

type Listener = {
  onSignedOut: () => void
  onNetwork: (online: boolean) => void
  onCall: () => void
}
let listener: Listener = { onSignedOut: () => {}, onNetwork: () => {}, onCall: () => {} }
export const listen = (l: Listener): void => { listener = l }

type Init = { readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE'; readonly json?: unknown; readonly form?: FormData; readonly quiet401?: boolean; readonly signal?: AbortSignal }

/** Same-origin JSON call. A 204 answers `null`. A 401 `signedOut` tells the app, unless `quiet401`. */
export async function api<T>(path: string, init: Init = {}): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  let body: BodyInit | undefined
  if (init.json !== undefined) {
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(init.json)
  } else if (init.form) body = init.form
  let response: Response
  try {
    response = await fetch(path, { method: init.method ?? (body ? 'POST' : 'GET'), headers, body: body ?? null, credentials: 'same-origin', cache: 'no-store', signal: init.signal ?? null })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error
    listener.onNetwork(false)
    throw new NetworkError('network')
  }
  listener.onNetwork(true)
  listener.onCall()
  if (response.status === 204) return null as T
  const data: unknown = await response.json().catch(() => null)
  if (!response.ok) {
    const record = data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
    const code = typeof record['error'] === 'string' ? record['error'] : `http${response.status}`
    if (response.status === 401 && code === 'signedOut' && !init.quiet401) listener.onSignedOut()
    throw new ApiError(response.status, code, record)
  }
  return data as T
}

// ───────────── formatting ─────────────

const locale = (lang: Lang): string => (lang === 'es' ? 'es-ES' : 'en-GB')

/** A date-only ISO string (`2026-11-04`) is a calendar day, never shifted by time zone. */
export const toDate = (iso: string): Date => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(iso)
}
const clean = (s: string): string => s.replace(/\./g, '').replace(/\b([Ss])ept\b/g, '$1ep').replace(/^(\p{L}+), /u, '$1 ')

/** 1 mar 2027 / 1 Mar 2027 */
export const fmtDate = (iso: string, lang: Lang): string =>
  clean(new Intl.DateTimeFormat(locale(lang), { day: 'numeric', month: 'short', year: 'numeric' }).format(toDate(iso)))
/** 1 mar / 1 Mar */
export const fmtDay = (iso: string, lang: Lang): string =>
  clean(new Intl.DateTimeFormat(locale(lang), { day: 'numeric', month: 'short' }).format(toDate(iso)))
/** Febrero 2027 / February 2027 */
export const fmtMonth = (iso: string, lang: Lang): string => {
  const d = toDate(iso)
  const month = new Intl.DateTimeFormat(locale(lang), { month: 'long' }).format(d)
  return month.charAt(0).toUpperCase() + month.slice(1) + ' ' + d.getFullYear()
}
/** martes 10 nov / Tuesday 10 Nov */
export const fmtWeekday = (iso: string, lang: Lang): string =>
  clean(new Intl.DateTimeFormat(locale(lang), { weekday: 'long', day: 'numeric', month: 'short' }).format(toDate(iso)))
export const fmtTime = (iso: string | Date, lang: Lang): string =>
  new Intl.DateTimeFormat(locale(lang), { hour: '2-digit', minute: '2-digit' }).format(typeof iso === 'string' ? new Date(iso) : iso)

const sameDay = (a: Date, b: Date): boolean => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
/** "hoy a las 9:12", "ayer a las 9:12" or "3 nov 2026" */
export const fmtWhen = (iso: string, lang: Lang): string => {
  const d = new Date(iso)
  const now = new Date()
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1)
  const at = fmtTime(d, lang)
  if (sameDay(d, now)) return lang === 'es' ? `hoy a las ${at}` : `today at ${at}`
  if (sameDay(d, yesterday)) return lang === 'es' ? `ayer a las ${at}` : `yesterday at ${at}`
  return fmtDate(iso, lang)
}
export const capital = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1)

export const fmtInt = (n: number, lang: Lang): string =>
  new Intl.NumberFormat(locale(lang), { maximumFractionDigits: 0, useGrouping: 'always' }).format(n)
export const fmtMoney = (n: number, currency: string, lang: Lang, decimals = 2): string =>
  new Intl.NumberFormat(locale(lang), { style: 'currency', currency, minimumFractionDigits: decimals, maximumFractionDigits: decimals, useGrouping: 'always' }).format(n)
/** 7,1 % / 7.1% */
export const fmtPct = (n: number, lang: Lang, decimals = 0): string => {
  const v = new Intl.NumberFormat(locale(lang), { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(n)
  return lang === 'es' ? `${v} %` : `${v}%`
}
export const daysUntil = (iso: string): number => {
  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((toDate(iso).getTime() - today.getTime()) / 86_400_000)
}
export const addDays = (iso: string, days: number): string => {
  const d = toDate(iso)
  d.setDate(d.getDate() + days)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
export const mmss = (seconds: number): string => `${Math.floor(seconds / 60)}:${String(Math.max(0, seconds % 60)).padStart(2, '0')}`

// ───────────── browser ─────────────

export type Platform = 'ios' | 'android' | 'other'
export const platform: Platform = (() => {
  const ua = navigator.userAgent
  if (/iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'ios'
  if (/Android/.test(ua)) return 'android'
  return 'other'
})()

export const reducedMotion = (): boolean => {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches } catch { return false }
}

/** localStorage can be missing or throw (private mode, blocked storage): every use is wrapped. */
export const store = {
  get(key: string): string | null {
    try { return window.localStorage.getItem(key) } catch { return null }
  },
  set(key: string, value: string): void {
    try { window.localStorage.setItem(key, value) } catch { /* storage unavailable: the app works without it */ }
  },
  remove(key: string): void {
    try { window.localStorage.removeItem(key) } catch { /* storage unavailable */ }
  },
}

/** The first 6-digit number in a message body, for the demo inbox's "use this code". */
export const codeIn = (text: string): string | null => /\b(\d{6})\b/.exec(text)?.[1] ?? null
